var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState, useMemo, forwardRef, } from 'react';
import ColorUtils from 'color';
import { Button as ChakraButton, Flex, useTheme, } from '@chakra-ui/react';
import BEIcon from '@molecules/BEIcon';
import { ButtonSizes } from '@theme/components/Button';
import { useAppSize } from '@view/hooks/useAppSize';
import { spaceRelativeMultiplier, } from '@theme/foundations/space';
export const BEButton = forwardRef((_a, ref) => {
    var { align = 'center', borderRadius = 'full', children, color = 'neutral', icon, label, testid, variant = 'solid', weight = 500, size } = _a, props = __rest(_a, ["align", "borderRadius", "children", "color", "icon", "label", "testid", "variant", "weight", "size"]);
    const theme = useTheme();
    const buttonRef = useRef(null);
    const { fontSize } = useAppSize();
    // size props, used to sanitize width/height/padding values
    const { h, height, w, width, minW, minWidth, px, paddingX, py, paddingY, p, padding, } = props;
    const textColor = useMemo(() => {
        if (variant === 'outline' && color === 'primary') {
            return 'neutral.900';
        }
        // Evaluate the ACTUAL solid fill (primary.500), not primary[600]: keying
        // off the darker 600 shade picked white text against light-teal brands,
        // giving ~2:1 label contrast. Deciding on the real fill flips light brands
        // to dark text so the label meets 1.4.3. BENG-4171 #30.
        return color === 'primary' && variant === 'solid'
            ? ColorUtils(theme.colors.primary[500]).isDark()
                ? 'white'
                : 'neutral.900'
            : undefined;
    }, [color, theme.colors.primary, variant]);
    // Workaround for Chakra Button size props being defined in px
    // When clients override the base fontSize, these fixed values break the intended relative scaling.
    // This logic converts the theme spacing values into relative units based on the root current fontSize.
    const sanitizedSizeProps = useMemo(() => {
        var _a;
        const toRelativeSize = (key) => { var _a; return `calc(${(_a = spaceRelativeMultiplier[key]) !== null && _a !== void 0 ? _a : 1} * ${fontSize})`; };
        const isSpaceKey = (value) => Object.keys(spaceRelativeMultiplier).includes((value === null || value === void 0 ? void 0 : value.toString()) || '');
        const resolve = (input, fallbackKey) => {
            if (input == null || input === undefined)
                return fallbackKey ? toRelativeSize(fallbackKey) : undefined;
            return isSpaceKey(input) ? toRelativeSize(input) : input;
        };
        const fallback = size ? (_a = ButtonSizes[size]) !== null && _a !== void 0 ? _a : ButtonSizes.md : {};
        const resolvedW = resolve(w !== null && w !== void 0 ? w : width);
        const resolvedH = resolve(h !== null && h !== void 0 ? h : height, fallback.h);
        const resolvedP = resolve(p !== null && p !== void 0 ? p : padding, fallback.p);
        const resolvedPx = resolve(px !== null && px !== void 0 ? px : paddingX, fallback.px);
        const resolvedPy = resolve(py !== null && py !== void 0 ? py : paddingY, fallback.py);
        const resolvedMinW = resolve(minW !== null && minW !== void 0 ? minW : minWidth, fallback.minW);
        return {
            h: resolvedH,
            height: resolvedH,
            minW: resolvedMinW,
            minWidth: resolvedMinW,
            p: resolvedP,
            padding: resolvedP,
            paddingX: resolvedPx,
            paddingY: resolvedPy,
            px: resolvedPx,
            py: resolvedPy,
            w: resolvedW,
            width: resolvedW,
        };
    }, [
        size,
        w,
        width,
        h,
        height,
        px,
        paddingX,
        py,
        paddingY,
        minW,
        minWidth,
        fontSize,
        p,
        padding,
    ]);
    return (_jsx(ChakraButton, Object.assign({}, props, sanitizedSizeProps, { borderRadius: borderRadius, color: textColor, colorScheme: color, "data-testid": testid, fontWeight: weight, justifyContent: align, ref: ref || buttonRef, size: size, textDecorationLine: variant === 'link' ? 'underline' : 'none', variant: variant, children: _jsxs(Flex, { alignItems: "center", className: "cb-text", gap: 1, justifyContent: align, minWidth: 0, children: [icon && _jsx(BEIcon, { "aria-hidden": true, className: "cb-icon", name: icon }), label, children] }) })));
});
export const BELoadingButton = (_a) => {
    var { children, color = 'primary', disabled = false, onAfterClick, onBeforeClick, onClick, testid } = _a, props = __rest(_a, ["children", "color", "disabled", "onAfterClick", "onBeforeClick", "onClick", "testid"]);
    const isMountedRef = useRef(true);
    const [confirmLoading, setConfirmLoading] = useState(!!props.isLoading);
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    useEffect(() => {
        setConfirmLoading(!!props.isLoading);
    }, [props.isLoading]);
    const handleClick = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!confirmLoading) {
            yield (onClick === null || onClick === void 0 ? void 0 : onClick());
        }
    }), [confirmLoading, onClick]);
    const onClickButton = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        onBeforeClick === null || onBeforeClick === void 0 ? void 0 : onBeforeClick();
        setConfirmLoading(true);
        try {
            yield handleClick();
        }
        finally {
            if (isMountedRef.current)
                setConfirmLoading(false);
        }
        onAfterClick === null || onAfterClick === void 0 ? void 0 : onAfterClick();
    }), [handleClick, onAfterClick, onBeforeClick]);
    return (_jsx(BEButton, Object.assign({ color: color, isDisabled: disabled, isLoading: confirmLoading, minWidth: 24, onClick: onClickButton, testid: testid }, props, { children: children })));
};
export default BEButton;
